import * as DialogPrimitive from "@radix-ui/react-dialog";
import React from "react";

import { cn } from "../cn";
import { X } from "../icons";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Overlay>, React.ComponentProps<typeof DialogPrimitive.Overlay>>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-slot="dialog-overlay"
    className={cn(
      "tw-fixed tw-inset-0 tw-z-50 tw-bg-backdrop",
      "data-[state=open]:tw-animate-in data-[state=open]:tw-fade-in-0",
      "data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

export const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, React.ComponentProps<typeof DialogPrimitive.Content> & { showCloseButton?: boolean }>(
  ({ className, children, showCloseButton = true, ...props }, ref) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-slot="dialog-content"
        className={cn(
          "tw-fixed tw-left-1/2 tw-top-1/2 tw-z-50 -tw-translate-x-1/2 -tw-translate-y-1/2",
          "tw-flex tw-max-h-[88vh] tw-w-[calc(100%-2rem)] tw-max-w-lg tw-flex-col tw-gap-4 tw-overflow-y-auto",
          "tw-rounded-xl tw-border tw-border-solid tw-border-border tw-bg-background tw-p-8 tw-shadow-lg",
          "data-[state=open]:tw-animate-in data-[state=open]:tw-fade-in-0 data-[state=open]:tw-zoom-in-95",
          "data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=closed]:tw-zoom-out-95",
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton ? (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="tw-absolute tw-right-4 tw-top-4 tw-cursor-pointer tw-rounded-md tw-border-0 tw-bg-transparent tw-p-1 tw-text-muted-foreground tw-outline-none tw-transition-colors hover:tw-bg-accent hover:tw-text-foreground focus-visible:tw-ring focus-visible:tw-ring-ring/50"
          >
            <X className="tw-size-4" />
            <span className="tw-sr-only">Закрыть</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = "DialogContent";

export function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="dialog-header" className={cn("tw-flex tw-flex-col tw-gap-2 tw-text-center", className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="dialog-footer" className={cn("tw-flex tw-flex-col tw-gap-2", className)} {...props} />;
}

export function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title data-slot="dialog-title" className={cn("tw-m-0 tw-text-xl tw-font-medium tw-text-foreground", className)} {...props} />;
}

export function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description data-slot="dialog-description" className={cn("tw-m-0 tw-text-sm tw-text-muted-foreground", className)} {...props} />;
}
